import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';

import { DeliveryService } from 'src/app/services/delivery.service';
import { NotificationService } from 'src/app/shared/notification.service';

// Triage for problems reported from the field. A driver standing at a broken
// machine already told us what is wrong — this screen is where somebody OWNS
// that report: fix it, dispatch a technician, or close it with a note.
//
// Lists are plain fields fed by live snapshots (never template getters — the
// four-times-frozen-dashboard rule).

interface IssueRow {
    id: string;
    systemName: string;
    categoryLabel: string;
    note: string;
    driverName: string;
    when: string;
    date: string;
    resolvedBy?: string;
    resolvedWhen?: string;
    resolution?: string;
}

const CATEGORY_LABELS: Record<string, string> = {
    machine: 'Machine down',
    payment: 'Payment terminal',
    temperature: 'Temperature',
    damage: 'Damage',
    access: 'Access',
    other: 'Other'
};

@Component({
    selector: 'app-delivery-issues',
    templateUrl: './issues.component.html',
    styleUrls: ['./issues.component.css']
})
export class DeliveryIssuesComponent implements OnInit, OnDestroy {

    tab: 'open' | 'resolved' = 'open';
    loading = true;
    rows: IssueRow[] = [];
    openCount = 0;

    resolvingId: string | null = null;
    resolutionNote = '';

    private sub: Subscription | null = null;
    private countSub: Subscription | null = null;

    constructor(private svc: DeliveryService, private notifier: NotificationService) { }

    ngOnInit() {
        this.subscribe();
        this.countSub = this.svc.issues$('open').subscribe(list => this.openCount = list.length);
    }

    ngOnDestroy() {
        this.sub?.unsubscribe();
        this.countSub?.unsubscribe();
    }

    setTab(tab: 'open' | 'resolved') {
        if (this.tab === tab) return;
        this.tab = tab;
        this.subscribe();
    }

    private subscribe() {
        this.sub?.unsubscribe();
        this.loading = true;
        this.sub = this.svc.issues$(this.tab).subscribe(list => {
            this.rows = list.map(i => ({
                id: i.id,
                systemName: i.systemName || i.systemId || '—',
                categoryLabel: CATEGORY_LABELS[i.category] || i.category || 'Other',
                note: i.note || '',
                driverName: i.driverName || i.driverUid || '',
                when: i.createdAt ? moment.unix(i.createdAt).format('HH:mm') : '',
                date: i.createdAt ? moment.unix(i.createdAt).format('DD.MM.YYYY') : '',
                resolvedBy: i.resolvedBy || '',
                resolvedWhen: i.resolvedAt ? moment.unix(i.resolvedAt).format('DD.MM HH:mm') : '',
                resolution: i.resolution || ''
            }));
            this.loading = false;
        }, () => { this.loading = false; });
    }

    startResolve(row: IssueRow) {
        this.resolvingId = this.resolvingId === row.id ? null : row.id;
        this.resolutionNote = '';
    }

    async confirmResolve(row: IssueRow) {
        try {
            await this.svc.resolveIssue(row.id, this.resolutionNote.trim() || null);
            this.notifier.showNotification('success', 'Issue resolved');
            this.resolvingId = null;
        } catch (err: any) {
            this.notifier.showNotification('error', err?.message || 'Could not resolve the issue');
        }
    }

    async reopen(row: IssueRow) {
        try {
            await this.svc.reopenIssue(row.id);
            this.notifier.showNotification('success', 'Issue reopened');
        } catch (err: any) {
            this.notifier.showNotification('error', err?.message || 'Could not reopen the issue');
        }
    }

    trackByIssue(_: number, r: IssueRow) { return r.id; }
}
